// ヒストグラムの設定UIコンポーネント - タブ式UI

import React, { useState } from 'react';
import { GraphConfigProps, HistogramConfig } from '../../../../types/graph-builder.types';
import { createColumnId } from '../../../../types';
import { getIntervalRatioColumns, getCategoricalColumns } from '../../../utils/graph-builder-utils';
import * as styles from '../../../styles/components/graph-builder.css.ts';

export function HistogramConfigComponent({
  config,
  dataset,
  onChange
}: GraphConfigProps<HistogramConfig>) {
  const [activeTab, setActiveTab] = useState<'basic' | 'grouping' | 'annotations'>('basic');
  
  // interval/ratio尺度の数値列のみを取得（ヒストグラム用）
  const numericColumns = getIntervalRatioColumns(dataset);
  // カテゴリカル列を取得（グループ化用）
  const categoricalColumns = getCategoricalColumns(dataset);

  const handleColumnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({
      ...config,
      column: createColumnId(e.target.value)
    });
  };

  const handleBinsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const bins = parseInt(e.target.value, 10);
    if (!isNaN(bins)) {
      onChange({
        ...config,
        bins: Math.max(1, Math.min(100, bins))
      });
    }
  };

  const handleShowDensityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...config,
      showDensity: e.target.checked
    });
  };

  const handleOrientationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({
      ...config,
      orientation: e.target.value as 'vertical' | 'horizontal'
    });
  };

  const handleGroupByColumnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({
      ...config,
      groupByColumn: e.target.value ? createColumnId(e.target.value) : undefined
    });
  };

  const handleGroupModeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({
      ...config,
      groupMode: e.target.value as 'overlay' | 'stacked' | 'faceted'
    });
  };

  const handleShowMeanChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...config,
      showAnnotations: {
        ...config.showAnnotations,
        mean: e.target.checked
      }
    });
  };

  const handleShowMedianChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...config,
      showAnnotations: {
        ...config.showAnnotations,
        median: e.target.checked
      }
    });
  };

  return (
    <div className={styles.tabContainer}>
      {/* タブヘッダー */}
      <div className={styles.tabHeader}>
        <button
          className={`${styles.tab} ${activeTab === 'basic' ? styles.active : ''}`}
          onClick={() => setActiveTab('basic')}
        >
          Basic
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'grouping' ? styles.active : ''}`}
          onClick={() => setActiveTab('grouping')}
        >
          Grouping
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'annotations' ? styles.active : ''}`}
          onClick={() => setActiveTab('annotations')}
        >
          Annotations
        </button>
      </div>

      {/* タブコンテンツ */}
      <div className={styles.tabContent}>
        {/* Basic Tab */}
        {activeTab === 'basic' && (
          <div>
            <div className={styles.settingItem}>
              <label className={styles.settingLabel}>Column</label>
              <select
                className={styles.settingSelect}
                value={config.column || ''}
                onChange={handleColumnChange}
                data-testid="histogram-column-select"
              >
                <option value="">Select a column</option>
                {numericColumns.length === 0 ? (
                  <option value="" disabled>No numeric columns with interval/ratio scale</option>
                ) : (
                  numericColumns.map(col => (
                    <option key={col.id} value={col.id}>
                      {col.name} {col.scale?.scale ? `(${col.scale.scale})` : ''}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div className={styles.settingItem}>
              <label className={styles.settingLabel}>Bins</label>
              <input
                type="number"
                min="1"
                max="100"
                value={config.bins}
                onChange={handleBinsChange}
                className={styles.settingInput}
              />
            </div>

            <div className={styles.settingItem}>
              <label className={styles.settingLabel}>Orientation</label>
              <select
                value={config.orientation || 'vertical'}
                onChange={handleOrientationChange}
                className={styles.settingSelect}
              >
                <option value="vertical">Vertical</option>
                <option value="horizontal">Horizontal</option>
              </select>
            </div>

            <div className={styles.settingItem}>
              <label className={styles.settingCheckbox}>
                <input
                  type="checkbox"
                  checked={config.showDensity || false}
                  onChange={handleShowDensityChange}
                />
                Show Density Curve
              </label>
            </div>
          </div>
        )}

        {/* Grouping Tab */}
        {activeTab === 'grouping' && (
          <div>
            <div className={styles.settingItem}>
              <label className={styles.settingLabel}>Group By</label>
              <select
                value={config.groupByColumn || ''}
                onChange={handleGroupByColumnChange}
                className={styles.settingSelect}
              >
                <option value="">No grouping</option>
                {categoricalColumns.map(col => (
                  <option key={col.id} value={col.id}>
                    {col.name}
                  </option>
                ))}
              </select>
            </div>

            {config.groupByColumn && (
              <div className={styles.settingItem}>
                <label className={styles.settingLabel}>Group Mode</label>
                <select
                  value={config.groupMode || 'overlay'}
                  onChange={handleGroupModeChange}
                  className={styles.settingSelect}
                >
                  <option value="overlay">Overlay</option>
                  <option value="stacked">Stacked</option>
                  <option value="faceted">Faceted</option>
                </select>
              </div>
            )}

            {!config.groupByColumn && (
              <div className={styles.hint}>
                Select a categorical column above to enable grouping options.
              </div>
            )}
          </div>
        )}

        {/* Annotations Tab */}
        {activeTab === 'annotations' && (
          <div>
            <div className={styles.settingItem}>
              <label className={styles.settingCheckbox}>
                <input
                  type="checkbox"
                  checked={config.showAnnotations?.mean || false}
                  onChange={handleShowMeanChange}
                />
                Show Mean (solid line)
              </label>
            </div>

            <div className={styles.settingItem}>
              <label className={styles.settingCheckbox}>
                <input
                  type="checkbox"
                  checked={config.showAnnotations?.median || false}
                  onChange={handleShowMedianChange}
                />
                Show Median (dashed line)
              </label>
            </div>

            <div className={styles.hint}>
              Statistical annotations display reference lines and labels on the histogram.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}